import { useSortable } from "@dnd-kit/react/sortable";
import { Grip, Pencil, Plus, Trash } from "@procore/core-icons";
import {
   Box,
   Button,
   colors,
   Flex,
   spacing,
   Typography,
   useI18nContext,
} from "@procore/core-react";
import React, { useEffect, useRef, useState } from "react";
import { InlineInput } from "./inline-input";
import type { ActiveRow, ProjectCategoryNode } from "./types";
import { BORDER, CATEGORIES_TRANSLATIONS, DropZoneIntent, MAX_DEPTH } from "./utils";

const { ON } = DropZoneIntent;
const categoryRowIndent = spacing.sm;
const categoryDropIntoFillColor = colors.blue85;

type CategoryRowProps = {
   node: ProjectCategoryNode;
   index: number;
   matched?: boolean;
   showSubcategories: boolean;
   activeRow: ActiveRow | null;
   setActiveRow: (row: ActiveRow | null) => void;
   onCreate: (params: { name: string; parent_id: string | null }) => void;
   onRename: (params: { id: string; name: string }) => void;
   onDelete: (params: { id: string }) => void;
   isDeleting: boolean;
   isCreating: boolean;
   isRenaming: boolean;
   dragEnabled?: boolean;
   dropIntent: DropZoneIntent | null;
};

export const CategoryRow = ({
   node,
   index,
   matched = true,
   showSubcategories,
   activeRow,
   setActiveRow,
   onCreate,
   onRename,
   onDelete,
   isDeleting,
   isCreating,
   isRenaming,
   dragEnabled = false,
   dropIntent,
}: CategoryRowProps) => {
   const I18n = useI18nContext();
   const [renameValue, setRenameValue] = useState(node.name);
   const [newChildName, setNewChildName] = useState("");

   const isRenameInputOpen = activeRow?.mode === "rename" && activeRow.id === node.id;
   const isDeleteConfirmOpen = activeRow?.mode === "delete" && activeRow.id === node.id;
   const isAddChildInputOpen = activeRow?.mode === "create" && activeRow.parentId === node.id;

   const isRoot = node.depth === 0;
   const deleteRef = useRef<HTMLDivElement>(null);

   useEffect(() => {
      if (isDeleteConfirmOpen) deleteRef.current?.scrollIntoView({ block: "nearest" });
   }, [isDeleteConfirmOpen]);

   const handleRenameSave = () => {
      if (renameValue.trim()) onRename({ id: node.id, name: renameValue.trim() });
   };

   const handleAddChildSave = () => {
      if (newChildName.trim()) onCreate({ name: newChildName.trim(), parent_id: node.id });
   };

   const { ref, handleRef, isDragSource } = useSortable({
      id: node.id,
      index,
      disabled: !dragEnabled,
      plugins: [],
      transition: null,
      data: {
         id: node.id,
         name: node.name,
         sequence: node.sequence,
         parent_id: node.parent_id,
         depth: node.depth,
         descendantCount: node.descendantCount,
      },
   });

   return (
      <>
         <Flex
            ref={ref}
            alignItems="center"
            justifyContent="space-between"
            style={{
               borderBottom: BORDER,
               opacity: isDragSource ? 0.35 : 1,
               transition: "background-color 150ms ease, box-shadow 150ms ease",
               backgroundColor: dropIntent === ON ? categoryDropIntoFillColor : "",
            }}
         >
            <Flex
               ref={handleRef}
               role="button"
               tabIndex={0}
               aria-label={I18n.t(`${CATEGORIES_TRANSLATIONS}.aria_drag_handle`)}
               paddingTop="md"
               paddingRight="xs"
               paddingBottom="md"
               style={{
                  paddingLeft: Math.max(node.depth * categoryRowIndent, spacing.xs),
                  cursor: dragEnabled ? "grab" : "",
                  touchAction: "none",
                  color: colors.gray60,
                  background: colors.gray98,
                  borderRight: `1px solid ${colors.gray85}`,
               }}
            >
               {dragEnabled ? <Grip /> : <Box style={{ width: spacing.xl, height: spacing.xl }} />}
            </Flex>
            {isRenameInputOpen ? (
               <Flex
                  paddingTop="xs"
                  paddingRight="lg"
                  paddingBottom="xs"
                  paddingLeft="lg"
                  style={{
                     paddingLeft: Math.max(node.depth * categoryRowIndent, spacing.md),
                     flex: 1,
                  }}
               >
                  <InlineInput
                     value={renameValue}
                     onChange={setRenameValue}
                     onSave={handleRenameSave}
                     onCancel={() => setActiveRow(null)}
                     isSaving={isRenaming}
                     ariaLabel={I18n.t(
                        `${CATEGORIES_TRANSLATIONS}.${
                           isRoot ? "aria_category_name" : "aria_subcategory_name"
                        }`,
                     )}
                  />
               </Flex>
            ) : (
               <>
                  <Flex
                     gap="lg"
                     paddingTop="md"
                     paddingRight="lg"
                     paddingBottom="md"
                     paddingLeft="lg"
                     style={{
                        flex: 1,
                        minWidth: 0,
                     }}
                  >
                     <Typography weight={matched ? "semibold" : undefined}>{node.name}</Typography>
                     {isRoot && !!node.descendantCount && (
                        <Typography color="gray45">
                           {I18n.t(`${CATEGORIES_TRANSLATIONS}.subcategory_count`, {
                              count: node.descendantCount,
                           })}
                        </Typography>
                     )}
                  </Flex>
                  <Flex gap="sm" paddingLeft="lg" paddingRight="lg">
                     {showSubcategories && node.depth < MAX_DEPTH && (
                        <Button
                           aria-label={I18n.t(`${CATEGORIES_TRANSLATIONS}.add_subcategory`)}
                           variant="tertiary"
                           onClick={() => {
                              setNewChildName("");
                              setActiveRow({ id: node.id, mode: "create", parentId: node.id });
                           }}
                           icon={<Plus />}
                        />
                     )}
                     <Button
                        aria-label={I18n.t(
                           `${CATEGORIES_TRANSLATIONS}.${
                              isRoot ? "rename_category" : "rename_subcategory"
                           }`,
                        )}
                        variant="tertiary"
                        onClick={() => {
                           setRenameValue(node.name);
                           setActiveRow({ id: node.id, mode: "rename" });
                        }}
                        icon={<Pencil />}
                     />
                     <Button
                        aria-label={I18n.t(
                           `${CATEGORIES_TRANSLATIONS}.${
                              isRoot ? "aria_delete_category" : "aria_delete_subcategory"
                           }`,
                        )}
                        variant="tertiary"
                        onClick={() => setActiveRow({ id: node.id, mode: "delete" })}
                        icon={<Trash />}
                     />
                  </Flex>
               </>
            )}
         </Flex>

         {showSubcategories && isAddChildInputOpen && (
            <Flex alignItems="center" style={{ borderBottom: BORDER }}>
               <Flex
                  paddingTop="md"
                  paddingRight="xs"
                  paddingBottom="md"
                  style={{
                     paddingLeft: Math.max((node.depth + 1) * categoryRowIndent, spacing.xs),
                     background: colors.gray98,
                     borderRight: `1px solid ${colors.gray85}`,
                  }}
               >
                  <Box style={{ width: spacing.xl, height: spacing.xl }} />
               </Flex>
               <Flex paddingTop="xs" paddingRight="lg" paddingBottom="xs" paddingLeft="lg">
                  <InlineInput
                     value={newChildName}
                     onChange={setNewChildName}
                     onSave={handleAddChildSave}
                     onCancel={() => setActiveRow(null)}
                     isSaving={isCreating}
                     placeholder={I18n.t(`${CATEGORIES_TRANSLATIONS}.new_category_placeholder`)}
                     ariaLabel={I18n.t(`${CATEGORIES_TRANSLATIONS}.aria_new_subcategory`)}
                  />
               </Flex>
            </Flex>
         )}

         {isDeleteConfirmOpen && (
            <Box ref={deleteRef} padding="md" style={{ borderBottom: BORDER }}>
               <Typography intent="h1" as="h1" weight="bold">
                  {I18n.t(
                     `${CATEGORIES_TRANSLATIONS}.${
                        isRoot ? "delete_category" : "delete_subcategory"
                     }`,
                  )}
               </Typography>
               <Typography as="p" style={{ marginBottom: 12 }}>
                  {I18n.t(`${CATEGORIES_TRANSLATIONS}.delete_description`, {
                     name: node.name,
                     type: isRoot ? "category" : "subcategory",
                  })}
               </Typography>
               <Typography as="p">
                  {I18n.t(`${CATEGORIES_TRANSLATIONS}.delete_cannot_undo`)}
               </Typography>
               <Flex justifyContent="flex-end" gap="sm">
                  <Button variant="secondary" size="sm" onClick={() => setActiveRow(null)}>
                     {I18n.t(`${CATEGORIES_TRANSLATIONS}.cancel`)}
                  </Button>
                  <Button loading={isDeleting} size="sm" onClick={() => onDelete({ id: node.id })}>
                     {I18n.t(`${CATEGORIES_TRANSLATIONS}.delete`)}
                  </Button>
               </Flex>
            </Box>
         )}
      </>
   );
};
